/**
 * Zoom im Viewer: Stufen für die Tasten, Pinch mit zwei Fingern und ein Anker,
 * der den Punkt unter den Fingern (bzw. die Bildschirmmitte) beim Zoomen festhält.
 */

export const ZOOM_MAX = 2.5;
export const ZOOM_MIN = 0.5;
/** Doppelseite: 100 % = beide Seiten passen genau auf den Bildschirm */
export const SPREAD_ZOOM_MIN = 1;

export function clampZoom(value: number, min = ZOOM_MIN, max = ZOOM_MAX): number {
  if (!Number.isFinite(value)) return min;
  return Math.round(Math.min(max, Math.max(min, value)) * 100) / 100;
}

/** Nächste Zehnerstufe in die gewünschte Richtung (1,23 → 1,3 bzw. 1,2). */
export function stepZoom(current: number, direction: 1 | -1, min = ZOOM_MIN, max = ZOOM_MAX): number {
  const tenths = current * 10;
  const next = direction > 0 ? Math.floor(tenths + 1e-6) + 1 : Math.ceil(tenths - 1e-6) - 1;
  return clampZoom(next / 10, min, max);
}

interface XY {
  clientX: number;
  clientY: number;
}

export function distance(a: XY, b: XY): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

export function midpoint(a: XY, b: XY): { x: number; y: number } {
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
}

/** Zoom während einer Pinch-Geste, relativ zum Zoom beim Aufsetzen der Finger. */
export function pinchZoom(
  startZoom: number,
  startDistance: number,
  currentDistance: number,
  min = ZOOM_MIN,
  max = ZOOM_MAX,
): number {
  if (startDistance <= 0) return clampZoom(startZoom, min, max);
  return clampZoom((startZoom * currentDistance) / startDistance, min, max);
}

export interface Box {
  page: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Punkt im Inhalt, festgehalten relativ zu einer Seite: welche Seite, wo darauf
 * (0–1) und wo er auf dem Bildschirm liegt.
 */
export interface ZoomAnchor {
  page: number;
  fx: number;
  fy: number;
  screenX: number;
  screenY: number;
}

/**
 * Sucht die Seite unter dem Punkt (Inhaltskoordinaten) – oder die nächstgelegene –
 * und liefert die relative Lage darauf.
 */
export function captureAnchor(
  boxes: readonly Box[],
  x: number,
  y: number,
  screenX: number,
  screenY: number,
): ZoomAnchor | null {
  let best: Box | null = null;
  let bestDistance = Infinity;
  for (const box of boxes) {
    const dx = x < box.left ? box.left - x : x > box.left + box.width ? x - box.left - box.width : 0;
    const dy = y < box.top ? box.top - y : y > box.top + box.height ? y - box.top - box.height : 0;
    const d = Math.hypot(dx, dy);
    if (d < bestDistance) {
      best = box;
      bestDistance = d;
      if (d === 0) break;
    }
  }
  if (!best) return null;
  return {
    page: best.page,
    fx: (x - best.left) / Math.max(1, best.width),
    fy: (y - best.top) / Math.max(1, best.height),
    screenX,
    screenY,
  };
}

/** Scrollposition, die den Anker nach dem Zoomen wieder unter denselben Bildschirmpunkt legt. */
export function anchoredScroll(anchor: ZoomAnchor, box: Box): { left: number; top: number } {
  return {
    left: Math.max(0, box.left + anchor.fx * box.width - anchor.screenX),
    top: Math.max(0, box.top + anchor.fy * box.height - anchor.screenY),
  };
}
